import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk, AppThunkGeneric } from "@/app/store";
import { fetchProductStoreOrders } from "../api/fetchProductStoreOrders/fetchProductStoreOrdersApi";
import {
  FetchProductStoreOrdersRequest,
  FetchProductStoreOrdersResponse,
  ProductStoreOrderListItem,
} from "../api/fetchProductStoreOrders/types";
import { updateProductStoreOrderStatus } from "../api/updateProductStoreOrderStatus/updateProductStoreOrderStatusApi";
import { UpdatableOrderStatus } from "../api/updateProductStoreOrderStatus/types";

interface ProductStoreOrdersState {
  orders: ProductStoreOrderListItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: ProductStoreOrdersState = {
  orders: [],
  totalCount: 0,
  loading: true,
  error: null,
};

const productStoreOrdersSlice = createSlice({
  name: "productStoreOrders",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setOrdersData: (
      state,
      action: PayloadAction<{
        orders: ProductStoreOrderListItem[];
        totalCount: number;
      }>,
    ) => {
      state.orders = action.payload.orders;
      state.totalCount = action.payload.totalCount;
    },
    patchOrder: (state, action: PayloadAction<ProductStoreOrderListItem>) => {
      const next = action.payload;
      const idx = state.orders.findIndex((o) => o.uuid === next.uuid);
      if (idx !== -1) {
        state.orders[idx] = next;
      }
    },
    resetProductStoreOrders: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setOrdersData,
  patchOrder,
  resetProductStoreOrders,
} = productStoreOrdersSlice.actions;

export const fetchProductStoreOrdersAction =
  (
    requestData: FetchProductStoreOrdersRequest,
    signal?: AbortSignal,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchProductStoreOrdersResponse =
        await fetchProductStoreOrders(requestData, signal);

      dispatch(
        setOrdersData({
          orders: response.data || [],
          totalCount: response.meta?.totalCount || 0,
        }),
      );
    } catch (error) {
      if (error instanceof Error && error.name === "CanceledError") {
        return;
      }
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch orders"));
      }
    } finally {
      if (!signal?.aborted) {
        dispatch(setLoading(false));
      }
    }
  };

export const updateProductStoreOrderStatusAction =
  (
    uuid: string,
    status: UpdatableOrderStatus,
    sendNotification: boolean,
  ): AppThunkGeneric<Promise<void>> =>
  async (dispatch: AppDispatch) => {
    const response = await updateProductStoreOrderStatus(uuid, {
      status,
      sendNotification,
    });
    dispatch(patchOrder(response.data));
  };

export default productStoreOrdersSlice.reducer;
